/**
 * Validador de RUT chileno (FEAT-065).
 *
 * Reglas:
 *  - Formato aceptado: `12345678-9`, `12.345.678-9`, `123456789` (sin separadores).
 *  - Cuerpo: 7 u 8 dígitos.
 *  - Dígito verificador: 0–9 o K (mayúscula o minúscula).
 *  - DV se valida con módulo 11 (algoritmo SII).
 *
 * Helpers:
 *  - `cleanRut(rut)`: remueve puntos y guión, normaliza DV a mayúscula.
 *  - `formatRut(rut)`: formatea como `12.345.678-9` (con puntos y guión).
 *  - `validateRut(rut)`: true si formato + DV son válidos.
 */

const RUT_BODY_MIN = 7;
const RUT_BODY_MAX = 8;

/**
 * Limpia un RUT removiendo puntos y guión, y normaliza DV a mayúscula.
 */
export function cleanRut(rut: string): string {
  if (!rut) return "";
  return rut.replace(/[.-]/g, "").toUpperCase();
}

/**
 * Calcula el dígito verificador esperado para el cuerpo de un RUT.
 * @internal
 */
function computeDv(body: string): string {
  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  const expected = 11 - (sum % 11);
  if (expected === 11) return "0";
  if (expected === 10) return "K";
  return String(expected);
}

/**
 * Valida un RUT chileno (formato + dígito verificador módulo 11).
 *
 * @example
 * validateRut("11.111.111-1") // true
 * validateRut("12345678-5") // true
 * validateRut("12345678-0") // false (DV incorrecto)
 */
export function validateRut(rut: string): boolean {
  if (typeof rut !== "string") return false;
  const clean = cleanRut(rut);
  if (!new RegExp(`^\\d{${RUT_BODY_MIN},${RUT_BODY_MAX}}[0-9K]$`).test(clean)) return false;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return computeDv(body) === dv;
}

/**
 * Formatea un RUT como `12.345.678-9`. Si el input es inválido en formato base
 * (cuerpo + DV detectable), retorna el input sin cambios.
 *
 * @example
 * formatRut("123456789") // "12.345.678-9"
 * formatRut("12345678-K") // "12.345.678-K"
 */
export function formatRut(rut: string): string {
  if (!rut) return "";
  const clean = cleanRut(rut);
  if (clean.length < RUT_BODY_MIN + 1 || clean.length > RUT_BODY_MAX + 1) return rut;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  // Insertar puntos cada 3 dígitos desde la derecha.
  const withDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${withDots}-${dv}`;
}
